import { inject, Injector } from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { UserStore } from '@quipex/core/auth';
import { FEATURE_FLAG, FeatureFlagService } from '@quipex/core/config';
import { qpxToast as toast } from '@quipex/core/utils';
import type { injectRequestAccessWorkflow } from '@quipex/feature/building-access-control/requests-runtime';
import { DialogService, injectPopover } from '@quipex/shared/ui';

export function injectRequestAccessPopover() {
    const popover = injectPopover();
    const user = inject(UserStore);
    return {
        open(
            origin: HTMLElement,
            workflow: ReturnType<typeof injectRequestAccessWorkflow>
        ) {
            return popover.open({
                origin,
                closeOnOutsidePointer: true,
                canDismiss: () => !workflow.submitting(),
                openBehavior: 'preserve',
                componentInputs: {
                    workflow,
                    organisationName: user.org()?.name ?? 'your organisation',
                },
                loadComponent: async () => {
                    try {
                        return (await import('./request-access-popover'))
                            .RequestAccessPopover;
                    } catch (error) {
                        toast.task(() => Promise.reject(error), {
                            error: {
                                defaultMessage:
                                    'Unable to open Request access. Try again.',
                            },
                        });
                        throw error;
                    }
                },
            });
        },
    };
}

/** Starts the request form as a dialog; true means a request was sent. */
export function injectRequestAccessDialog() {
    const dialog = inject(DialogService);
    const injector = inject(Injector);
    const enabled = inject(FeatureFlagService).flagEnabled(
        FEATURE_FLAG.BuildingAccessRequests
    );
    const user = inject(UserStore);
    return {
        async open(
            workflow: ReturnType<typeof injectRequestAccessWorkflow>
        ): Promise<MatDialogRef<unknown, boolean | undefined> | null> {
            const orgId = user.org()?.id;
            const { RequestAccessPopover } =
                await import('./request-access-popover');
            if (!enabled() || !orgId || orgId !== user.org()?.id) return null;
            const ref = dialog.openDialog(
                {
                    component: RequestAccessPopover,
                    componentInputs: {
                        workflow,
                        organisationName:
                            user.org()?.name ?? 'your organisation',
                        presentation: 'dialog',
                    },
                },
                {
                    size: 'sm',
                    panelClass: ['flex-content', 'auto-h'],
                    ariaLabel: 'Request access',
                    injector,
                    restoreFocus: true,
                    disableClose: false,
                    autoFocus: '#request-access-message',
                }
            ) as MatDialogRef<unknown, boolean | undefined>;
            return ref;
        },
        async openForBuilding(
            buildingId: string
        ): Promise<MatDialogRef<unknown, boolean | undefined> | null> {
            const orgId = user.org()?.id;
            const { RequestAccessDialog } =
                await import('./request-access-dialog/request-access-dialog');
            if (!enabled() || !orgId || orgId !== user.org()?.id) return null;
            const ref = dialog.openDialog(
                {
                    component: RequestAccessDialog,
                    componentInputs: {
                        buildingId,
                        organisationName:
                            user.org()?.name ?? 'your organisation',
                    },
                },
                {
                    size: 'sm',
                    panelClass: ['flex-content', 'auto-h'],
                    ariaLabel: 'Request access',
                    injector,
                    restoreFocus: true,
                    disableClose: false,
                    autoFocus: '#request-access-message',
                }
            ) as MatDialogRef<unknown, boolean | undefined>;
            return ref;
        },
    };
}
